// Map viewer: a level's terrain on a pannable, zoomable canvas.
// In M1 it is reached from the dev menu; M4/M5 build Planning and Playback on top of it.
import { useSignal } from '@preact/signals';
import { useEffect, useRef } from 'preact/hooks';
import { loadLevel, type LoadedLevel } from '../../data/levels';
import { MapRenderer } from '../../render/renderer';
import type { Ink } from '../../render/theme';
import { attachMapGestures } from '../input/gestures';
import { navigate } from '../state/screen';

function readInk(): Ink {
  const css = getComputedStyle(document.documentElement);
  return {
    ink: css.getPropertyValue('--ink').trim() || '#161616',
    paper: css.getPropertyValue('--paper').trim() || '#f5f3ee',
    font: css.getPropertyValue('--font-mono').trim() || 'monospace',
  };
}

async function load(id: string): Promise<LoadedLevel> {
  if (__DEV_TOOLS__) {
    const dev = await (await import('../../data/devLevels')).loadDevLevel(id);
    if (dev) return dev;
  }
  return loadLevel(id);
}

// Space the chrome takes (plus the notch / home indicator), so Fit frames the map in the clear area.
const CHROME = { top: 72, right: 12, bottom: 64, left: 12 };

/** Safe-area insets in CSS px, read from a probe element styled with env(). */
function safeInsets(probe: HTMLElement | null): typeof CHROME {
  if (!probe) return { top: 0, right: 0, bottom: 0, left: 0 };
  const cs = getComputedStyle(probe);
  const n = (v: string): number => Number.parseFloat(v) || 0;
  return {
    top: n(cs.paddingTop),
    right: n(cs.paddingRight),
    bottom: n(cs.paddingBottom),
    left: n(cs.paddingLeft),
  };
}

export function MapScreen({ levelId }: { levelId: string }) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rendererRef = useRef<MapRenderer | null>(null);
  const probeRef = useRef<HTMLDivElement>(null);
  const level = useSignal<LoadedLevel | null>(null);
  const error = useSignal<string | null>(null);
  const grid = useSignal(false);
  const debug = useSignal(false);

  // Canvas, renderer, gestures, resize and theme.
  useEffect(() => {
    const wrap = wrapRef.current;
    const canvas = canvasRef.current;
    if (!wrap || !canvas) return;
    const r = new MapRenderer(canvas);
    rendererRef.current = r;
    r.setInk(readInk());
    const size = (): void => {
      const safe = safeInsets(probeRef.current);
      r.setInsets({
        top: CHROME.top + safe.top,
        right: CHROME.right + safe.right,
        bottom: CHROME.bottom + safe.bottom,
        left: CHROME.left + safe.left,
      });
      const box = wrap.getBoundingClientRect();
      r.resize(box.width, box.height, window.devicePixelRatio || 1);
    };
    size();
    const ro = new ResizeObserver(size);
    ro.observe(wrap);
    const detach = attachMapGestures(canvas, r);
    const themeObs = new MutationObserver(() => r.setInk(readInk()));
    themeObs.observe(document.documentElement, { attributes: true });
    void document.fonts?.ready.then(() => r.invalidate());
    if (__DEV_TOOLS__) {
      (window as unknown as { __rt?: unknown }).__rt = { renderer: r };
    }
    return () => {
      ro.disconnect();
      themeObs.disconnect();
      detach();
      r.destroy();
      rendererRef.current = null;
      if (__DEV_TOOLS__) delete (window as unknown as { __rt?: unknown }).__rt;
    };
  }, []);

  // Level data.
  useEffect(() => {
    let live = true;
    level.value = null;
    error.value = null;
    load(levelId).then(
      (l) => {
        if (!live) return;
        level.value = l;
        rendererRef.current?.setMap(l.map);
      },
      (e: unknown) => {
        if (live) error.value = e instanceof Error ? e.message : String(e);
      },
    );
    return () => {
      live = false;
    };
  }, [levelId]);

  useEffect(() => {
    rendererRef.current?.setOptions({ grid: grid.value, debug: debug.value });
  }, [grid.value, debug.value]);

  return (
    <main class="map-screen" data-screen="map" data-level={levelId}>
      <div class="safe-probe" ref={probeRef} aria-hidden="true" />
      <div class="map-canvas-wrap" ref={wrapRef}>
        <canvas
          ref={canvasRef}
          class="map-canvas"
          data-testid="map-canvas"
          data-ready={level.value ? 'true' : 'false'}
          role="img"
          aria-label={level.value ? `Map: ${level.value.title}` : 'Map loading'}
        />
      </div>
      <header class="map-bar">
        <button
          type="button"
          class="chip chip-icon"
          aria-label="Back to Home"
          data-testid="map-back"
          onClick={() => navigate({ screen: 'home' })}
        >
          ‹
        </button>
        <h1 class="map-title">{level.value?.title ?? (error.value ? 'Map' : 'Loading…')}</h1>
        <div class="map-tools">
          <button
            type="button"
            class="chip"
            data-testid="map-fit"
            aria-label="Show the whole map"
            onClick={() => rendererRef.current?.fit()}
          >
            Fit
          </button>
          <button
            type="button"
            class="chip"
            aria-pressed={grid.value}
            data-testid="map-grid"
            aria-label="Hex grid"
            onClick={() => (grid.value = !grid.value)}
          >
            Grid
          </button>
          {__DEV_TOOLS__ && (
            <button
              type="button"
              class="chip"
              aria-pressed={debug.value}
              data-testid="map-debug"
              aria-label="Debug overlay: hex coordinates and elevation"
              onClick={() => (debug.value = !debug.value)}
            >
              Coords
            </button>
          )}
        </div>
      </header>
      {error.value && (
        <p class="map-error" role="alert">
          {error.value}
        </p>
      )}
    </main>
  );
}
